/**
 *  Plan state module file
 **/

import { PRODUCTS_TYPES } from '@/constants'
import { getAppMetadata, getAppFlavour } from '@/plan/metadata/metadata'
import { getDefaultSaveAsPublic } from '@/plan/metadata/metadata.helper'
import { Poi } from '@workspaces/types'
import type {
  PlanState,
  StatePlan,
  StateAssets,
  SubPlanFilter,
  PlanLegend,
  NewPoiDatasetForm,
  MapViewState,
} from './state.type'

// metadata information
const appMetadata = getAppMetadata()
const proximityDefaultParams = appMetadata.plan_filters.widget_proximity
const audienceDefaultParams = appMetadata.plan_filters.widget_audiences
const allCountries = appMetadata.countries
const baseMaps = appMetadata.app_config.basemaps
const defaultSelectedCountries = appMetadata.countries
  .filter((country) =>
    appMetadata.plan_filters.widget_countries.default_selection_iso3.includes(
      country.iso3,
    ),
  )
  .map((country) => country.id)

const MODEL_PROXIMITY_FILTER = () => ({
  distance: proximityDefaultParams.types.radius.selected_max_distance,
  min_distance: proximityDefaultParams.types.radius.selected_min_distance,
  max_distance: proximityDefaultParams.types.radius.selected_max_distance,
  max_num_panels: 0,
  method: proximityDefaultParams.types.radius.name,
  operation: Poi.POIsFilterOperation.Union,
  data: [],
  categoires: [],
  brands: [],
  tags: [],
})

export const MODEL_SUBPLAN_FILTERS = (): SubPlanFilter => ({
  tabname: '',
  countries: defaultSelectedCountries,
  geoboundaries: {
    region_1: [],
    region_2: [],
    region_3: [],
    region_4: [],
    region_5: [],
    region_6: [],
    region_7: [],
    region_8: [],
    region_9: [],
    region_10: [],
    region_11: [],
    region_12: [],
    region_15: [],
    region_16: [],
    region_17: [],
    region_18: [],
  },
  types: {
    [PRODUCTS_TYPES.L1_PANEL_CLASS]: [],
    [PRODUCTS_TYPES.L2_PRODUCT]: [],
    [PRODUCTS_TYPES.L3_SUBPRODUCT]: [],
    [PRODUCTS_TYPES.L4]: [],
    [PRODUCTS_TYPES.L5]: [],
    [PRODUCTS_TYPES.L6]: [],
    [PRODUCTS_TYPES.L7]: [],
    [PRODUCTS_TYPES.L8]: [],
    [PRODUCTS_TYPES.L9]: [],
  },
  [PRODUCTS_TYPES.IS_DIGITAL]: undefined,
  proximity: MODEL_PROXIMITY_FILTER(),
  assets_excluded: [],
  assets_included: [],
  panels: { assetsIds: [] },
  panelsExcluded: { assetsIds: [] },
  audiences: [],
  audiencesGroups: [],
  audiencesType: audienceDefaultParams.by_index.enable
    ? audienceDefaultParams.by_index.name
    : audienceDefaultParams.by_percentage.name,
  audiencesIndex: audienceDefaultParams.by_index.default_max_value,
  audiencesPercent: audienceDefaultParams.by_percentage.selected_max_value,
  userAudiencesGroup: [],
  delivery: null,
  polygon_geom: { type: 'FeatureCollection', features: [] },
  geographical_distribution: {
    optimizeBy: 'impressions',
    distanceFilterActive: false,
    distanceFilter: 3,
    maxFilterActive: false,
    maxFilters: {
      region_1: {
        active: false,
        max: 10,
      },
      region_2: {
        active: false,
        max: 10,
      },
      region_3: {
        active: false,
        max: 10,
      },
      region_4: {
        active: false,
        max: 10,
      },
      region_5: {
        active: false,
        max: 10,
      },
      region_6: {
        active: false,
        max: 10,
      },
      region_7: {
        active: false,
        max: 10,
      },
      region_8: {
        active: false,
        max: 10,
      },
      region_9: {
        active: false,
        max: 10,
      },
      region_10: {
        active: false,
        max: 10,
      },
      region_11: {
        active: false,
        max: 10,
      },
      region_12: {
        active: false,
        max: 10,
      },
    },
    regionExceptions: {},
  },
  audiencesQuintile: {
    quintiles: [1, 2, 3],
    audiences: [],
  },
  restrictions: [],
})

const MODEL_LEGEND = (): PlanLegend => ({
  productClass: 'l2product_id',
  hiddenProductsId: [],
  excludeAssets: false,
  canAssetsExcludedLayerBeShown: false,
  facingOrientationIcons: false,
  angleNorthMap: false,
  poisBufferVisibility: true,
  poisVisibility: true,
  boundariesVisibility: true,
  assetsIdsLayerVisibility: false,
  colorizeAssets: false,
  audiencesQuintile: true,
})

export const MODEL_PLAN = (): StatePlan => ({
  loading: false,
  dirty: false,
  filters: [MODEL_SUBPLAN_FILTERS()],
  proximity: {
    categories: [],
    brands: [],
    locations: [],
    tags: [],
  },
  created_by: null,
  count: 0,
  impressions: 0,
  legend: MODEL_LEGEND(),
  agency: null,
  custom_geoboundaries: [],
  custom_pois: [],
  brand: null,
  client: null,
  basemap: baseMaps.default,
  is_public: getDefaultSaveAsPublic(appMetadata),
})

export const MODEL_ASSETS = (): StateAssets => ({
  results: [],
  meta: {},
  count: 0,
  page: 1,
  page_size: 10,
  loading: false,
})

export const MODEL_NEW_POI_DATASET_FORM = (): NewPoiDatasetForm => ({
  id: null,
  type: appMetadata.features.manage_pois.default_type,
  name: '',
  description: '',
  shared: true,
  categories: [],
})

const initialViewState = (): MapViewState => ({
  longitude: 40.514933,
  latitude: -91.139458,
  zoom: 4,
  bearing: 0,
  pitch: 0,
})

export const state: PlanState = {
  flavour: getAppFlavour(),
  metadata: appMetadata,
  plan: MODEL_PLAN(),
  activeSubPlanFiltersIndex: 0,
  plan_backup: MODEL_PLAN(),
  assets: MODEL_ASSETS(),
  assetsProperties: {
    allDigital: false,
    allStatic: false,
  },
  mapUpdateFlags: {
    assets: false,
    pois: false,
    geoboundaries: false,
  },
  countries: allCountries,
  viewState: initialViewState(),
  proximityFlags: {
    open: {},
    selected: {},
    halfSelected: {},
    hidden: {},
  },
  openCustomGeometry: null,
  newPoiDatasetForm: MODEL_NEW_POI_DATASET_FORM(),
  fixme: null,
  latestdelivery: null,
  assetsFinishedLoad: false,
  geographicalDistributionRegions: [],
  loadingGeographicalDistributionRegions: false,
  assetsDetails: null,
  defaultUserCountries: [2],
  assetsReadyWithAllProperties: false,
  highlightedAssets: null,
  forceLogout: false,
  poisColors: {},
  geoBoundariesGeometriesCache: [],
  plans: {
    subscribed: false,
    user: [],
    public: [],
  },
  aiAgent: {
    waitingResponse: false,
  },
}
